import { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { palette } from "../../styles/constants";
import { useAccordionContext, useAccordionItemContext } from "./Context";

type AccordionPanelProps = {
    children: React.ReactNode;
    unstyled?: boolean;
    indent?: string | number;
};

export default function AccordionPanel({
    children,
    unstyled,
    indent,
}: AccordionPanelProps) {
    const { value } = useAccordionItemContext();
    const ctx = useAccordionContext();
    const isActive = ctx.isItemActive(value);
    const [openHeight, setOpenHeight] = useState(0);
    const panelRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const element = panelRef.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                if (panelRef.current) {
                    setOpenHeight(panelRef.current.scrollHeight);
                }
            });
        });

        resizeObserver.observe(element);

        requestAnimationFrame(() => {
            setOpenHeight(element.scrollHeight);
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Panel
            id={ctx.getRegionId(value)}
            data-active={isActive}
            data-unstyled={unstyled}
            role="region"
            ref={panelRef}
            openHeight={openHeight}
            style={{
                ...(indent && {
                    paddingLeft: indent,
                }),
            }}
        >
            {children}
        </Panel>
    );
}

type PanelProps = {
    openHeight: number;
};

const Panel = styled.div.attrs((props) => ({
    ...props,
    className: "accordion-panel",
}))<PanelProps>`
    width: 100%;
    transition: all 0.2s cubic-bezier(0.39, 0.575, 0.565, 1);
    overflow: hidden;
    &[data-active='false'] {
        pointer-events: none;
        user-select: none;
        max-height: 0px;
        opacity: 0;
    }
    &[data-active='true'] {
        pointer-events: auto;
        user-select: auto;
        max-height: ${(props) => props.openHeight}px;
        opacity: 1;
        background-color: ${palette.neutral[50]};
    }
    &[data-unstyled='true'] {
        background-color: unset;
    }
`;
